import { useAuthCtx } from '@/app/context/auth/authContextController/AuthContextController';
import useGetUserProfile from '@/features/accounts/hooks/useGetUserProfile';
import { usePermissions } from '@/features/auth/access-control/context/PermissionsProvider';
import {
  getVisibleSubMenuItems,
  normalizePath,
  sectionIsVisible,
  SubMenuItem,
} from '@/features/navigation/sidebar/sidebar.selectors';
import { useDynamicLocale } from '@/hoslog-intl';
import { Logo } from '@/icons';
import { isPathAllowed, VISIBLE_SIDEBAR_SECTIONS } from '@/lib/parseEnvList';
import { Link, useChildMatches } from '@tanstack/react-router';
import * as React from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from 'ui/sidebar';
import { TruncatedTooltipValue } from '../layout/TruncatedTooltipValue';
import { NavUser } from './Navuser';

function LeftSidebar({
  visibleMenuItems,
}: {
  visibleMenuItems: ReadonlyArray<{ to: string; label: string; icon: React.ComponentType<{ className?: string }> }>;
}) {
  const { t } = useDynamicLocale();
  return (
    <Sidebar collapsible="none" className="bg-sidebar-rail !w-(--sidebar-width-icon)">
      <SidebarHeader className="px-3.5 py-4.25">
        <div className="bg-destructive rounded-sm size-9 aspect-square flex items-center justify-center">
          <Link to="/">
            <Logo className="text-sidebar-content" />
          </Link>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent className="pl-0">
            <SidebarMenu className="pt-9 pr-0 pl-3">
              {visibleMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      tooltip={{ children: t(item.label), hidden: false }}
                      asChild
                      variant="icon"
                      size="lg"
                    >
                      <Link
                        to={item.to}
                        className="[&.active]:bg-sidebar-hover [&.active]:border-l-2 border-destructive rounded-l-xs"
                      >
                        <Icon />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

function RightSidebar({
  groupLabel,
  visibleSubMenuItems,
}: {
  groupLabel: string;
  visibleSubMenuItems: ReadonlyArray<SubMenuItem>;
}) {
  const { userData } = useGetUserProfile();
  const { fullName, email } = useAuthCtx();
  const { t } = useDynamicLocale();
  return (
    <Sidebar collapsible="none" className="flex">
      <SidebarHeader className="p-3 pb-3.5">
        <NavUser
          user={{
            fullName: userData?.profileName ?? fullName ?? '',
            email: email ?? '',
            profileImage: userData?.profileImage?.path || undefined,
          }}
        />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>
            {groupLabel && (
              <TruncatedTooltipValue content={t(groupLabel)} className="max-w-[145px] text-muted-foreground" />
            )}
          </SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              {visibleSubMenuItems.map((item) => {
                const to = normalizePath(item.path);
                if (!to) return null;

                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      tooltip={{ children: t(item.id), hidden: false }}
                      asChild
                      size="lg"
                      className="[&.active]:bg-sidebar-hover"
                    >
                      <Link className="flex gap-2 items-center w-full min-w-0" to={to}>
                        {Icon && <Icon className="size-5 flex-shrink-0" />}
                        <TruncatedTooltipValue content={t(item.id)} className="max-w-[145px] font-medium" />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const childMatches = useChildMatches();
  const perms = usePermissions();

  const subMenuItems = React.useMemo(() => {
    const childRoutes = (childMatches?.[0]?.context as { childRoutes?: unknown } | undefined)?.childRoutes;
    return Array.isArray(childRoutes) ? (childRoutes as SubMenuItem[]) : [];
  }, [childMatches]);

  const visibleSubMenuItems = React.useMemo(() => {
    const envFiltered = subMenuItems.filter((item) => isPathAllowed(normalizePath(item.path)));
    return getVisibleSubMenuItems(envFiltered, perms);
  }, [subMenuItems, perms]);

  const groupLabel = (childMatches?.[0]?.context as { groupLabel?: string } | undefined)?.groupLabel;

  const visibleMenuItems = React.useMemo(
    () => VISIBLE_SIDEBAR_SECTIONS.filter((item) => sectionIsVisible(item.to, perms)),
    [perms],
  );

  const showRightSidebar = visibleSubMenuItems.length > 0;

  return (
    <Sidebar collapsible="icon" className="overflow-hidden [&>[data-sidebar=sidebar]]:flex-row" {...props}>
      <LeftSidebar visibleMenuItems={visibleMenuItems} />
      {showRightSidebar && <RightSidebar groupLabel={groupLabel ?? ''} visibleSubMenuItems={visibleSubMenuItems} />}
    </Sidebar>
  );
}
